/*!
 * index.js — игра «маджонг-пасьянс».
 *
 * ── Четыре решения, ради которых всё это написано ─────────────────────────
 *
 * 1. ТУПИК ВИДЕН СРАЗУ. Раздача всегда собирается (generate.js), но игрок
 *    хоронит нужную кость своей же парой и узнаёт об этом через двадцать
 *    ходов. Замер на 20 партиях случайной игры: 11 из 20 упираются в тупик,
 *    в среднем на 33-м ходу из 72. Жанр отвечает на это кнопкой «перемешать»,
 *    то есть стирает партию. Здесь решатель в воркере проверяет позицию после
 *    КАЖДОГО хода (медиана 0 мс, потолок 800 мс) и, если выхода больше нет,
 *    предлагает вернуться к последнему положению, из которого он был.
 *
 * 2. ПОДСКАЗКА НЕ ВРЁТ. Обычная подсказка показывает любую совпавшую пару и
 *    спокойно заводит в тупик. Здесь она — первый ход выигрышной линии,
 *    найденной решателем.
 *
 * 3. НА ТАП ПО ЗАБЛОКИРОВАННОЙ КОСТИ ЕСТЬ ОТВЕТ. В нашей телеметрии главный
 *    источник ярости на досочных тулах — клик, на который ничего не
 *    происходит (/chess: 950 rage-кликов по пустым div за 20 дней). В
 *    маджонге свободны единицы костей из 144, то есть молчание — это ответ по
 *    умолчанию. Здесь кость подсвечивает СВОИХ блокировщиков и говорит, чем
 *    именно она занята, — причём говорит У САМОЙ КОСТИ, а не строкой под
 *    доской, в другом конце экрана от того места, куда человек ткнул.
 *
 * 4. ИНТЕРФЕЙС ДЕРЖИТСЯ НА ДВУХ ПАНЕЛЯХ. Раньше над доской стояли семь кнопок
 *    и шесть настроек в один ряд; всё, чем пользуются раз в партию, теперь
 *    живёт в выдвижных панелях «Новая партия» и «Настройки», а на экране
 *    остаётся то, чем играют.
 *
 * Перемешивание тоже честное: остаток раскладывается заново так, чтобы доска
 * снова собиралась, а не «как повезёт».
 */
import { LAYOUTS, PACKS, positionsOf, boundsOf, layoutMeta } from './layouts.js';
import { matchGroup } from './tiles.js';
import { makeIndex, isFree, blockReason, availableMoves, remaining } from './board.js';
import { deal, reshuffle } from './generate.js';
import { tileEl, tileName, thumbSvg, hasCjk } from './render.js';

const T = window.__MJ_I18N || {};
const t = (k, fb) => (T[k] !== undefined ? T[k] : (fb !== undefined ? fb : k));
const $ = (id) => document.getElementById(id);
const SLUG = 'mahjong';
const mg = (x) => matchGroup(x.id);
const SKINS = ['ivory', 'jade', 'night', 'contrast'];
const reduced = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const S = {
    layout: 'turtle',
    level: 'normal',
    seed: 0,
    tiles: [],
    history: [],      // [[ia, ib], ...]
    redo: [],
    selected: null,
    startedAt: 0,
    elapsed: 0,
    ticking: false,
    shuffles: 0,
    hints: 0,
    score: 0,
    combo: 0,
    lastTake: 0,
    worker: null,
    gen: 0,
    lastSafe: 0,      // глубина истории, на которой позиция ещё была собираема
    verdict: 'unknown',
    daily: false,
    won: false,
    overflowSent: false,
    fsReal: false,   // настоящий fullscreen получен (на iPhone его не бывает)
    hintMove: null,
    hintPending: false,
    hintResolve: null,
    hintTimer: 0,
    solverTimer: 0,
    solverDown: false,
    pickLayout: 'turtle',
    pickLevel: 'normal',
};

/* ── звук и отклик ────────────────────────────────────────────────────────
   Контекст создаётся только по жесту: до жеста он всё равно suspended, а
   висящий suspended-контекст на части машин потом не оживает (см. общий
   разбор __liveAudioCtxReady). Здесь звук — украшение, поэтому все ошибки
   гасятся молча. */
let actx = null;
function blip(freq, dur) {
    if (!settings.sound) return;
    try {
        actx = actx || new (window.AudioContext || window.webkitAudioContext)();
        if (actx.state === 'suspended') actx.resume();
        const o = actx.createOscillator(), g = actx.createGain();
        o.type = 'sine'; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, actx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.06, actx.currentTime + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, actx.currentTime + dur);
        o.connect(g); g.connect(actx.destination);
        o.start(); o.stop(actx.currentTime + dur + 0.02);
    } catch (e) { /* звук не обязателен */ }
}
/** Короткая вибрация — единственный отклик, который работает в тишине и
    вслепую. Есть не везде: строка настройки показывается только там, где есть. */
const canVibe = () => typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
function vibe(ms) { if (settings.vibe && canVibe()) { try { navigator.vibrate(ms); } catch (e) { } } }

/* ── не гасить экран ──────────────────────────────────────────────────────
 *
 * Партия идёт минутами без единого касания — телефон гасит экран ровно на
 * размышлении. Держит его ОБЩИЙ примитив `window.__wakeLock`, а не своя копия,
 * и это не вопрос стиля.
 *
 * Своя копия здесь была написана ровно с тем дефектом, из-за которого примитив
 * и появился: `if (on && !wakeLock)` спрашивает «есть ли у меня ОБЪЕКТ», а не
 * «держится ли ещё блокировка». Часовой, которого система уже отпустила,
 * остаётся правдивым объектом, а замороженная или вернувшаяся из bfcache
 * страница события `release` не получает вовсе — то есть переспросить некому,
 * и экран гаснет при включённом тумблере. Примитив вместо этого ПЕРЕВЫВОДИТ
 * состояние из наблюдаемого (`sentinel.released`, ход `currentTime` у
 * запасного видео), сам чинит потерю по сторожевому таймеру и умеет запасной
 * путь там, где API нет вовсе. Сторож — WakeLockPrimitiveTest.
 *
 * ⚠️ Safari берёт блокировку только по жесту, поэтому `request()` зовётся из
 * нажатия (тумблер, «Начать»), а не из visibilitychange: чинить возврат на
 * вкладку — работа примитива, у него на это есть активация страницы. */
const canWake = () => !!window.__wakeLock;
function setWake(on) {
    const w = window.__wakeLock;
    if (!w) return;
    try {
        if (on) w.hold('mahjong');
        else w.releaseHold('mahjong');
    } catch (e) { /* отказ платформы — не повод падать */ }
}

/* ── настройки ────────────────────────────────────────────────────────── */
const settings = {
    sound: false, codes: false, insurance: true, showFree: false,
    skin: 'ivory', timer: true, vibe: true, wake: false, zoom: 100,
};

function loadSettings() {
    const ts = window.__toolSettings;
    if (!ts) return;
    const b = (k, d) => ts.load(SLUG, k, d ? '1' : '0') === '1';
    settings.sound = b('sound', false);
    settings.codes = ts.load(SLUG, 'codes', hasCjk() ? '0' : '1') === '1';
    // Страховка от тупика раньше была списком из двух значений; тумблер
    // читает и старое значение, чтобы настройка не «слетала» у вернувшихся.
    settings.insurance = ts.load(SLUG, 'insurance', 'warn') !== 'off';
    settings.showFree = b('showfree', false);
    settings.timer = b('timer', true);
    settings.vibe = b('vibe', true);
    settings.wake = b('wake', false);
    const sk = ts.load(SLUG, 'skin', 'ivory');
    settings.skin = SKINS.indexOf(sk) >= 0 ? sk : 'ivory';
    const z = parseInt(ts.load(SLUG, 'zoom', '100'), 10);
    settings.zoom = z >= 60 && z <= 220 ? z : 100;
}
function saveSetting(k, v) {
    settings[k] = v;
    window.__toolSettings && window.__toolSettings.save(
        SLUG, k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
}

/* ── партия ───────────────────────────────────────────────────────────── */

function newGame(opts = {}) {
    const layout = opts.layout || S.layout;
    const level = opts.level || S.level;
    const seed = opts.seed !== undefined ? opts.seed : (Math.random() * 2147483647) | 0;
    const pos = positionsOf(layout);
    const d = deal(pos, seed, level);
    if (!d) {
        window.__toast && window.__toast(t('deal_failed', 'Не удалось разложить — попробуйте ещё раз'), 'error');
        return;
    }
    Object.assign(S, {
        layout, level, seed, daily: !!opts.daily,
        tiles: d.tiles, history: [], redo: [], selected: null,
        startedAt: Date.now(), elapsed: 0, ticking: true,
        shuffles: 0, hints: 0, score: 0, combo: 0, lastTake: 0,
        lastSafe: 0, verdict: 'solvable', won: false, overflowSent: false,
    });
    hideWin();
    buildBoard(true);
    startWorker();
    refresh();
    saveGame();
    if (settings.wake) setWake(true);
    window.__stage && window.__stage('game_start');
    window.__aeFunnel && window.__aeFunnel('game_start', {
        layout, level, size: S.tiles.length, daily: !!opts.daily,
    });
}

function buildBoard(animate) {
    const host = $('mj-board');
    /* Подсказка живёт ВНУТРИ доски (её координаты — координаты кости), поэтому
       перед очисткой её надо вынести: иначе первая же пересборка доски удалит
       элемент вместе с костями, и объяснение «чем занята кость» просто
       перестанет появляться — молча, потому что showTip() не найдёт узла. */
    const tipEl = $('mj-tip');
    if (tipEl && tipEl.parentElement === host) host.parentElement.appendChild(tipEl);
    host.textContent = '';
    const b = boundsOf(S.tiles);
    host.dataset.layout = S.layout;
    const frag = document.createDocumentFragment();
    S.tiles.forEach((tile, n) => {
        const el = tileEl(tile, T);
        if (animate && !reduced()) {
            el.classList.add('mj-in');
            // Ступенчатый вход: 144 кости, поставленные разом, читаются как
            // «страница дёрнулась»; те же кости, легшие волной за 400 мс, —
            // как раздача. Задержка идёт от НИЖНИХ слоёв к верхним.
            el.style.setProperty('--d', (tile.z * 60 + (n % 24) * 8) + 'ms');
        }
        frag.appendChild(el);
    });
    host.appendChild(frag);
    // Подсказка живёт ВНУТРИ доски: тогда её координаты — координаты кости, и
    // она едет вместе с прокруткой, а не остаётся висеть над пустым местом.
    const tip = $('mj-tip');
    if (tip && tip.parentElement !== host) host.appendChild(tip);
    host.classList.toggle('mj-codes', settings.codes);
    fitBoard();
    if (animate && !reduced()) {
        setTimeout(() => {
            host.querySelectorAll('.mj-in').forEach((e) => { e.classList.remove('mj-in'); e.style.removeProperty('--d'); });
        }, 900);
    }
    void b;
}

/* ── полноэкранный режим ───────────────────────────────────────────────────
 *
 * На телефоне полная фигура не влезает в ширину экрана ни при каком масштабе:
 * нижний предел размера кости (30x35 px, чтобы в неё можно было попасть)
 * умножается на 15–16 клеток в ширину. Ниже этого предела доска складывается
 * в кашу, поэтому остаётся прокрутка — а прокручивать поле, по которому
 * играешь тапами, неудобно вдвойне.
 *
 * Полноэкранный режим забирает под доску всё место сразу. Реализован КЛАССОМ,
 * а Fullscreen API идёт сверху как улучшение: на iPhone настоящего fullscreen
 * для произвольного элемента НЕТ, а именно телефон — главный адресат режима.
 * Запрос идёт через window.__safeFullscreen: сырой requestFullscreen()
 * реджектится Промисом, мимо try/catch (конвенция — FullscreenSafeConventionTest).
 */
const fsActive = () => { const w = $('mj'); return !!w && w.classList.contains('is-fs'); };

/* Пересчёт доски, когда коробка меняет размер не по нашей вине: появился
   баннер тупика, съехала клавиатура, повернули телефон, вышли из fullscreen
   кнопкой браузера. */
let boxRO = null, boxW = 0, boxH = 0;
function observeBox() {
    const box = $('mj-box');
    if (!box || typeof ResizeObserver === 'undefined' || boxRO) return;
    boxRO = new ResizeObserver(() => {
        const r = box.getBoundingClientRect();
        const w = Math.round(r.width), h = Math.round(r.height);
        if (w === boxW && h === boxH) return;   // тот же размер — не гоняем круг
        boxW = w; boxH = h;
        fitBoard();
    });
    boxRO.observe(box);
}

function updateFsBtn() {
    const on = fsActive();
    const lbl = $('mj-fs-lbl'), ico = $('mj-fs-ico'), btn = $('mj-fs');
    if (lbl) lbl.textContent = on ? t('fullscreen_exit', 'Обычный режим') : t('fullscreen', 'Во весь экран');
    if (ico) ico.firstElementChild.setAttribute('href', on ? '#mji-min' : '#mji-full');
    if (btn) btn.setAttribute('aria-pressed', on ? 'true' : 'false');
}

function enterFs() {
    const wrap = $('mj');
    if (!wrap || fsActive()) return;
    wrap.classList.add('is-fs');
    document.documentElement.classList.add('mj-fs-lock');
    updateFsBtn();
    fitBoard();
    const ask = window.__safeFullscreen ? window.__safeFullscreen(wrap) : Promise.resolve(false);
    ask.then((real) => {
        S.fsReal = !!real;
        window.__aeFunnel && window.__aeFunnel('fullscreen_on', {
            real: real ? 1 : 0, layout: S.layout, w: window.innerWidth, h: window.innerHeight,
        });
        // Настоящий fullscreen меняет размер окна асинхронно — второй замер
        // после того, как он состоялся.
        setTimeout(fitBoard, 120);
    });
}

function exitFs(fromBrowser) {
    const wrap = $('mj');
    if (!wrap || !fsActive()) return;
    wrap.classList.remove('is-fs');
    document.documentElement.classList.remove('mj-fs-lock');
    updateFsBtn();
    if (!fromBrowser && (document.fullscreenElement || document.webkitFullscreenElement)) {
        try {
            const r = (document.exitFullscreen || document.webkitExitFullscreen).call(document);
            if (r && r.catch) r.catch(() => { });
        } catch (_) { /* отказ выйти — не повод падать: класс уже снят */ }
    }
    S.fsReal = false;
    window.__aeFunnel && window.__aeFunnel('fullscreen_off', { via: fromBrowser ? 'browser' : 'button', layout: S.layout });
    fitBoard();
    setTimeout(fitBoard, 160);
}

const toggleFs = () => (fsActive() ? exitFs(false) : enterFs());

/** Масштаб доски под размер сцены. */
let fitting = false;
function fitBoard() {
    const host = $('mj-board');
    if (!host || !S.tiles.length || fitting) return;
    fitting = true;
    try { fitBoardInner(host); } finally { fitting = false; }
}
function fitBoardInner(host) {
    const b = boundsOf(S.tiles);
    /* Меряем СОДЕРЖИМОЕ коробки, а не её габарит: getBoundingClientRect даёт
       рамку с падингом и рамкой, поэтому доска, подогнанная «по box.width»,
       стабильно выходила шире места, в котором стоит, и коробка получала
       горизонтальную полосу там, где на экране было полно воздуха.
       clientWidth уже без рамки и без полосы прокрутки, остаётся снять падинг. */
    const par = host.parentElement;
    /* ⚠️ Высоту сцены ВСЕГДА меряем нетронутой.
     *
     * Ниже мы можем сжать сцену до высоты доски (см. «сцена отдаёт обратно»).
     * Если следующий замер возьмёт УЖЕ СЖАТУЮ высоту, он решит, что размер
     * упёрся в высоту, снимет сжатие, наблюдатель размера позовёт нас снова —
     * и сцена начнёт дышать без остановки. Поймано тем, что Playwright не смог
     * кликнуть по кости: «element is not stable». Поэтому сначала возвращаем
     * высоту из стилей и только потом меряем. */
    par.style.height = '';
    const cs = getComputedStyle(par);
    const box = {
        width: Math.max(0, par.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0)),
        height: Math.max(0, par.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0)),
    };
    /* Стопка не только занимает место, но и СМЕЩАЕТ его. Верхние слои уходят
       вправо на 0.3 клетки и ВВЕРХ на 0.5 — значит верхняя кость висит выше
       нулевой строки и без подъёма просто срезается краем. */
    const liftU = (b.layers - 1) * 0.5;          // подъём в полуклетках
    const growU = (b.layers - 1) * 0.3;          // уход вправо
    const padX = 2.35 + growU;
    const padY = 2.35 + liftU;
    const unitW = box.width / (b.w + padX);
    const unitH = Math.max(220, box.height) / (b.h + padY);
    /* НИЖНИЙ ПРЕДЕЛ размера кости — не украшение.
     *
     * «Вписать доску в ширину» на телефоне даёт кость 21×25 px: мельче ногтя,
     * попасть в неё нельзя, и общий accessibility-пол сайта (44 px на тач)
     * пытается это чинить, раздувая КАЖДУЮ кость — отчего доска, стоящая на
     * сетке в 21 px, складывается в кашу из наезжающих прямоугольников.
     *
     * Пол на кость применить нельзя: у доски геометрия и есть смысл. Поэтому
     * кости выведены из-под общего пола (`no-tap-floor`), а размер держит этот
     * предел. Что не влезло — прокручивается внутри своей коробки. Ручной
     * масштаб (настройка) предел ОБХОДИТ: это осознанный выбор игрока, а не
     * молчаливое умолчание. */
    const MIN_UNIT = 15;
    const fit = Math.min(unitW, unitH);
    const zoom = (settings.zoom || 100) / 100;
    const unit = Math.max(zoom < 1 ? 8 : MIN_UNIT, fit * zoom);
    host.style.setProperty('--u', unit + 'px');
    host.style.setProperty('--lift', (liftU * unit) + 'px');
    const fullW = (b.w + padX) * unit;
    const fullH = (b.h + padY) * unit;
    host.style.width = fullW + 'px';
    host.style.height = fullH + 'px';

    /* Сцена отдаёт обратно то, чего не заняла.
     *
     * Высота сцены задана от вьюпорта, а плоские фигуры («поле», «кот») ниже
     * широких: на телефоне под доской в 305 px оставалось 185 px пустого
     * сукна, и кнопки уезжали за сгиб ни за что. Сжимать можно ТОЛЬКО когда
     * размер упёрся в ширину: если он упёрся в высоту, сжатие уменьшит кость,
     * доска станет ниже, сцена сожмётся снова — и так по кругу. */
    if (!fsActive() && unitW < unitH * 0.995) {
        const padY2 = parseFloat(cs.paddingTop || 0) + parseFloat(cs.paddingBottom || 0);
        par.style.height = Math.ceil(fullH + padY2) + 'px';
    } else par.style.height = '';

    updateUnder(fullW > box.width + 2, unit, fullW, box.width);
}

/**
 * Полоса под доской. Она не «сообщение об ошибке», а ЕДИНСТВЕННОЕ место, где
 * человеку с телефона объясняют, почему фигура шире экрана, и предлагают
 * конкретный выход — не «поверните телефон» в пустоту, а кнопку.
 */
function updateUnder(tight, unit, fullW, boxWidth) {
    const box = $('mj-under'), txt = $('mj-under-t'), act = $('mj-under-act');
    if (!box) return;
    if (!tight) { box.hidden = true; return; }
    if (!S.overflowSent) {
        S.overflowSent = true;
        window.__aeFunnel && window.__aeFunnel('board_overflow', {
            layout: S.layout, u: Math.round(unit),
            boardW: Math.round(fullW), boxW: Math.round(boxWidth),
        });
    }
    box.hidden = false;
    const small = LAYOUTS.filter((l) => l.pack === 'quick').sort((a, b) => a.size - b.size)[0];
    if (!fsActive()) {
        txt.textContent = t('fit_fs', 'Фигура шире экрана. Во весь экран она станет крупнее — или возьмите короткую раскладку.');
        act.hidden = false;
        act.textContent = t('fullscreen', 'Во весь экран');
        act.onclick = enterFs;
    } else if (small && S.layout !== small.id) {
        txt.textContent = t('fit_small', 'Фигура шире экрана — её можно прокручивать. Для телефона есть короткие раскладки.');
        act.hidden = false;
        act.textContent = t('fit_small_btn', 'Короткая раскладка');
        act.onclick = () => { S.pickLayout = small.id; markPlayed(S.layout); newGame({ layout: small.id }); };
    } else {
        txt.textContent = t('fit_scroll', 'Доска шире экрана — её можно прокручивать.');
        act.hidden = true;
    }
}

/* ── ходы ─────────────────────────────────────────────────────────────── */

const elFor = (i) => $('mj-board').querySelector('.mj-tile[data-i="' + i + '"]');

function onTileClick(ev) {
    const btn = ev.target.closest('.mj-tile');
    if (!btn || S.won) return;
    const i = +btn.dataset.i;
    const tile = S.tiles[i];
    if (!tile || tile.removed) return;

    const index = makeIndex(S.tiles);
    const why = blockReason(tile, index);
    if (why) { explainBlocked(tile, why); return; }

    if (S.selected === i) { setSelected(null); return; }
    if (S.selected === null) { setSelected(i); blip(520, 0.05); return; }

    const other = S.tiles[S.selected];
    if (matchGroup(other.id) !== matchGroup(tile.id)) {
        // Не пара — переносим выбор, но говорим об этом: иначе выглядит так,
        // будто первый выбор «слетел» сам.
        flash(btn, 'mj-nomatch');
        flash(elFor(S.selected), 'mj-nomatch');
        const msg = t('no_match', 'Не пара: {a} и {b}')
            .replace('{a}', tileName(other.id, T)).replace('{b}', tileName(tile.id, T));
        showTip(tile, msg);
        announce(msg);
        setSelected(i);
        blip(220, 0.07);
        vibe(18);
        return;
    }
    takePair(S.selected, i);
}

function takePair(ia, ib) {
    S.tiles[ia].removed = true;
    S.tiles[ib].removed = true;
    S.history.push([ia, ib]);
    S.redo.length = 0;
    setSelected(null);
    hideTip();
    for (const i of [ia, ib]) {
        const el = elFor(i);
        if (el) { el.classList.add('is-going'); setTimeout(() => el.classList.add('is-gone'), 180); }
    }
    /* Счёт: пара — десять, серия подряд — надбавка. Серия рвётся паузой в
       шесть секунд, поэтому она награждает НАЙДЕННЫЙ ритм, а не скорость
       тыканья: подряд идущие очевидные пары человек и так видит. */
    const now = Date.now();
    S.combo = (S.lastTake && now - S.lastTake < 6000) ? S.combo + 1 : 0;
    S.lastTake = now;
    S.score += 10 + Math.min(S.combo * 4, 40);
    blip(S.combo ? 660 + Math.min(S.combo, 6) * 40 : 660, 0.07);
    vibe(12);
    refresh();
    saveGame();
    if (remaining(S.tiles) === 0) return win();
    askSolver();
}

function undo(fromDeadEnd) {
    if (!S.history.length || S.won) return;
    const [ia, ib] = S.history.pop();
    S.redo.push([ia, ib]);
    S.tiles[ia].removed = false;
    S.tiles[ib].removed = false;
    for (const i of [ia, ib]) {
        const el = elFor(i);
        if (el) el.classList.remove('is-going', 'is-gone');
    }
    S.combo = 0;
    setSelected(null);
    hideDeadEnd();
    refresh();
    saveGame();
    askSolver();
    window.__aeFunnel && window.__aeFunnel('undo', { fromDeadEnd: !!fromDeadEnd, left: remaining(S.tiles) });
}

function redoMove() {
    if (!S.redo.length || S.won) return;
    const [ia, ib] = S.redo.pop();
    S.tiles[ia].removed = true;
    S.tiles[ib].removed = true;
    S.history.push([ia, ib]);
    for (const i of [ia, ib]) {
        const el = elFor(i);
        if (el) el.classList.add('is-going', 'is-gone');
    }
    refresh();
    saveGame();
    askSolver();
}

/** Откатить до последнего положения, из которого доска ещё собиралась. */
function rewindToSafe() {
    const target = S.lastSafe;
    let guard = 0;
    while (S.history.length > target && guard++ < 400) {
        const [ia, ib] = S.history.pop();
        S.redo.push([ia, ib]);
        S.tiles[ia].removed = false;
        S.tiles[ib].removed = false;
        const ea = elFor(ia), eb = elFor(ib);
        if (ea) ea.classList.remove('is-going', 'is-gone');
        if (eb) eb.classList.remove('is-going', 'is-gone');
    }
    S.combo = 0;
    setSelected(null);
    hideDeadEnd();
    refresh();
    saveGame();
    askSolver();
    window.__aeFunnel && window.__aeFunnel('rewind_to_safe', { steps: guard });
}

function setSelected(i) {
    if (S.selected !== null) {
        const p = elFor(S.selected);
        if (p) { p.classList.remove('is-sel'); p.setAttribute('aria-pressed', 'false'); }
    }
    S.selected = i;
    if (i !== null) {
        const el = elFor(i);
        if (el) { el.classList.add('is-sel'); el.setAttribute('aria-pressed', 'true'); }
    }
    markPartners();
}

/**
 * Ходьба по доске стрелками.
 *
 * Кости — настоящие кнопки, поэтому Tab по ним работает и так; но их сто
 * сорок четыре, и «доступно с клавиатуры» в таком виде означает «доступно
 * теоретически». Стрелка переносит фокус на БЛИЖАЙШУЮ кость в своём
 * направлении — то есть по доске ходят так же, как смотрят: глазами.
 * У конкурентов доска вообще картинка, играть с клавиатуры нельзя нигде.
 */
function moveFocus(dx, dy) {
    const cur = document.activeElement;
    if (!cur || !cur.classList || !cur.classList.contains('mj-tile')) {
        const first = $('mj-board').querySelector('.mj-tile:not(.is-gone)');
        if (first) first.focus();
        return true;
    }
    const at = (el) => ({
        x: +el.style.getPropertyValue('--tx') + (+el.style.getPropertyValue('--tz')) * 0.6,
        y: +el.style.getPropertyValue('--ty') - (+el.style.getPropertyValue('--tz')),
    });
    const from = at(cur);
    let best = null, bestCost = Infinity;
    for (const el of $('mj-board').querySelectorAll('.mj-tile:not(.is-gone)')) {
        if (el === cur) continue;
        const p = at(el);
        const along = (p.x - from.x) * dx + (p.y - from.y) * dy;
        if (along <= 0.5) continue;                       // не в ту сторону
        const across = Math.abs((p.x - from.x) * dy) + Math.abs((p.y - from.y) * dx);
        // Вперёд идём дёшево, вбок — дорого: иначе стрелка «вправо» уводит по
        // диагонали через полдоски.
        const cost = along + across * 3;
        if (cost < bestCost) { bestCost = cost; best = el; }
    }
    if (best) { best.focus({ preventScroll: false }); return true; }
    return false;
}

/**
 * Пока кость выбрана — показать, с кем она СЕЙЧАС составляет пару.
 *
 * Иначе человек ищет партнёра глазами по 144 костям и половину времени тыкает
 * в занятые: это ровно тот тап без ответа, который в нашей телеметрии на
 * досочных тулах даёт основную массу rage-кликов. Подсказка тут ничего не
 * решает за игрока — она отвечает на вопрос «а этот вообще доступен».
 */
function markPartners() {
    const board = $('mj-board');
    if (!board) return;
    board.querySelectorAll('.mj-tile.is-partner').forEach((e) => e.classList.remove('is-partner'));
    if (S.selected === null) return;
    const sel = S.tiles[S.selected];
    const index = makeIndex(S.tiles);
    for (const x of S.tiles) {
        if (x.removed || x.i === sel.i) continue;
        if (matchGroup(x.id) !== matchGroup(sel.id)) continue;
        if (!isFree(x, index)) continue;
        const el = elFor(x.i);
        if (el) el.classList.add('is-partner');
    }
}

/** Пометить все свободные кости — по желанию игрока. */
function markFree() {
    const board = $('mj-board');
    if (!board) return;
    board.querySelectorAll('.mj-tile.is-free').forEach((e) => e.classList.remove('is-free'));
    if (!settings.showFree) { board.classList.remove('mj-showfree'); return; }
    board.classList.add('mj-showfree');
    const index = makeIndex(S.tiles);
    for (const x of S.tiles) {
        if (x.removed || !isFree(x, index)) continue;
        const el = elFor(x.i);
        if (el) el.classList.add('is-free');
    }
}

function flash(el, cls) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    setTimeout(() => el.classList.remove(cls), 700);
}

/**
 * Пояснение У САМОЙ КОСТИ.
 *
 * Раньше оно печаталось строкой под доской. На «черепахе» это до 600 px от
 * того места, куда человек только что ткнул, — то есть ответ был, но в другом
 * конце экрана, и читался как его отсутствие.
 */
let tipTimer = 0;
function showTip(tile, msg) {
    const tip = $('mj-tip'), host = $('mj-board');
    if (!tip || !host) return;
    const u = parseFloat(getComputedStyle(host).getPropertyValue('--u')) || 16;
    const lift = parseFloat(getComputedStyle(host).getPropertyValue('--lift')) || 0;
    tip.textContent = msg;
    tip.hidden = false;
    const x = tile.x * u + tile.z * u * 0.3 + u;
    const y = tile.y * u - tile.z * u * 0.5 + lift;
    // Держим подсказку внутри доски: у краёв она иначе уезжает за коробку и
    // обрезается — то есть сообщение снова не доходит.
    const w = tip.offsetWidth || 200;
    const clamped = Math.max(w / 2 + 4, Math.min(x, host.clientWidth - w / 2 - 4));
    tip.style.left = clamped + 'px';
    const above = y > tip.offsetHeight + 10;
    tip.style.top = (above ? y - 8 : y + u * 2.35 + 8) + 'px';
    tip.style.transform = 'translate(-50%,' + (above ? '-100%' : '0') + ')';
    clearTimeout(tipTimer);
    tipTimer = setTimeout(hideTip, 3600);
}
function hideTip() { const tip = $('mj-tip'); if (tip) tip.hidden = true; }

function explainBlocked(tile, why) {
    const el = elFor(tile.i);
    flash(el, 'mj-blocked');
    for (const b of why.by.slice(0, 4)) flash(elFor(b.i), 'mj-blocker');
    const msg = why.kind === 'covered'
        ? t('blocked_covered', '{tile} накрыта сверху — сначала снимите {by}')
            .replace('{tile}', tileName(tile.id, T)).replace('{by}', tileName(why.by[0].id, T))
        : t('blocked_squeezed', '{tile} зажата с обеих сторон — нужен свободный бок')
            .replace('{tile}', tileName(tile.id, T));
    showTip(tile, msg);
    announce(msg);
    blip(180, 0.06);
    vibe(28);
    window.__aeFunnel && window.__aeFunnel('blocked_tap', { kind: why.kind });
}

const announce = (m) => window.__announce && window.__announce(m);

/* ── решатель ─────────────────────────────────────────────────────────── */

function startWorker() {
    stopWorker();
    const url = window.__MJ_WORKER_URL;
    if (!url) { solverUnavailable('no_url'); return; }
    /* Движок без модульных воркеров (Firefox < 114, Safari < 15) известен заранее —
       общая тихая проба слоя; воркер тогда не грузится вовсе, а решатель честно «не
       берётся сказать». Решатель на главном потоке замерен и отвергнут (2026-10-08,
       3145 позиций случайной игры на 22 раскладках): со сроком 30 мс вердикт есть у
       92 % позиций, но сам срок решатель проверяет не на каждом шаге — p99 73 мс,
       худшее 207 мс на быстром десктопе, а старые движки живут на старом железе;
       замерший интерфейс в момент клика — ровно то, ради чего решатель в воркере. */
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) { solverUnavailable('module_worker'); return; }
    try {
        S.worker = new Worker(url, { type: 'module' });
    } catch (e) {
        S.worker = null;
        solverUnavailable('ctor:' + (e && e.message ? e.message.slice(0, 60) : '?'));
        return;
    }
    /* Молчащий воркер — самый дорогой отказ этого тула: главная его функция
       (слежение за тупиком и честная подсказка) просто исчезает, а на экране
       остаётся вечное «Проверяю…», то есть обещание, которое никто не
       выполнит. Поэтому у ответа есть срок, а у неудачи — своё событие. */
    S.worker.onerror = (ev) => {
        // Отказ ОБРАБОТАН (честное «не знаю» и своя телеметрия) — значит, его
        // гасят. Без preventDefault() непойманная ошибка воркера уходит ещё и в
        // window.onerror страницы: Firefox 111 не умеет module-воркеры, читает
        // скрипт как классический и падает на import — тул спокойно жил без
        // решателя, а Sentry получал «SyntaxError: import declarations may only
        // appear at top level of a module» (TIMBRICA-FRONTEND-P1). Тот же ход —
        // chess-stockfish.js.
        if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
        solverUnavailable('onerror:' + ((ev && ev.message) || 'unknown'));
        stopWorker();
    };
    S.worker.onmessage = (e) => {
        const m = e.data || {};
        if (m.type !== 'result') return;
        // Ответ на устаревшую позицию выбрасывается: игрок успевает сходить,
        // пока считается предыдущий ход, и предупреждение о тупике на доске,
        // которой уже нет, — хуже, чем его отсутствие.
        if (m.gen !== S.gen) return;
        clearTimeout(S.solverTimer);
        S.solverDown = false;
        // Меряем СМЕНУ вердикта, а не каждый ответ: иначе на партию улетает
        // 72 события об одном и том же.
        if (S.verdict !== m.status) {
            window.__aeFunnel && window.__aeFunnel('solver_verdict', {
                status: m.status, left: remaining(S.tiles), ms: m.ms || 0,
            });
        }
        S.verdict = m.status;
        if (S.hintResolve) {
            const done = S.hintResolve;
            S.hintResolve = null;
            clearTimeout(S.hintTimer);
            // Порядок важен: сперва обновим вердикт и подсветку, потом ответим
            // ожидающей подсказке — иначе она увидит устаревшее состояние.
            setTimeout(() => done(m.status === 'solvable' ? m.move : null), 0);
        }
        if (m.status === 'solvable') {
            S.lastSafe = S.history.length;
            S.hintMove = m.move;
            hideDeadEnd();
        } else if (m.status === 'dead') {
            S.hintMove = null;
            onDeadEnd();
        } else {
            S.hintMove = null;
        }
        renderVerdict();
    };
    S.gen++;
    S.worker.postMessage({
        type: 'init', gen: S.gen,
        tiles: S.tiles.map((x) => ({ x: x.x, y: x.y, z: x.z, id: x.id })),
    });
    askSolver();
}
function stopWorker() {
    if (S.worker) { try { S.worker.terminate(); } catch (e) { } S.worker = null; }
}

/**
 * Решателя нет или он замолчал. Игра продолжается — играть можно и без него,
 * — но обещание с экрана снимается: «не берусь сказать» вместо вечного
 * «проверяю». Врать про исправность того, чего нет, хуже, чем признаться.
 */
function solverUnavailable(why) {
    S.verdict = 'unknown';
    S.hintMove = null;
    renderVerdict();
    if (!S.solverDown) {
        S.solverDown = true;
        window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'solver_unavailable', why });
    }
}

function askSolver() {
    if (!S.worker) { solverUnavailable('no_worker'); return; }
    S.gen++;
    S.verdict = 'checking';
    renderVerdict();
    const asked = S.gen;
    clearTimeout(S.solverTimer);
    // Потолок сознательно много больше рабочего (замер: медиана 0 мс, хвост
    // 800 мс): срок нужен не для скорости, а чтобы «Проверяю…» не осталось
    // на экране навсегда, если воркер умер молча.
    S.solverTimer = setTimeout(() => {
        if (S.gen === asked && S.verdict === 'checking') solverUnavailable('timeout');
    }, 12000);
    S.worker.postMessage({
        type: 'check', gen: S.gen,
        alive: Uint8Array.from(S.tiles, (x) => (x.removed ? 0 : 1)),
        budget: 400000, deadlineMs: 800,
    });
}

function onDeadEnd() {
    if (!settings.insurance) return;
    const box = $('mj-dead');
    if (!box) return;
    const back = S.history.length - S.lastSafe;
    box.hidden = false;
    $('mj-dead-text').textContent = back > 0
        ? t('dead_text', 'Выхода из этой позиции больше нет. Последнее положение, из которого доска собиралась, было {n} ходов назад.')
            .replace('{n}', String(back))
        : t('dead_text_now', 'Выхода из этой позиции больше нет.');
    $('mj-rewind').hidden = back <= 0;
    announce(t('dead_aria', 'Тупик: собрать доску из этой позиции уже нельзя'));
    window.__aeFunnel && window.__aeFunnel('dead_end', { move: S.history.length, back });
}
function hideDeadEnd() { const b = $('mj-dead'); if (b) b.hidden = true; }
function hideWin() { const b = $('mj-win'); if (b) b.hidden = true; }

function useHint() {
    if (S.won) return;
    if (!S.hintMove) {
        /* Три РАЗНЫХ состояния, и их нельзя мешать.
         *
         * «Выигрышного хода отсюда нет» — правда только при вердикте `dead`.
         * При `unknown` это ложь: решатель просто не уложился в свой бюджет
         * (замер: так заканчиваются 4 % фоновых проверок). Сказать человеку
         * «хода нет» там, где он есть, — худшее, что может сделать подсказка,
         * ради честности которой всё и написано.
         *
         * И у явной просьбы бюджет должен быть ДРУГОЙ: фоновая проверка идёт
         * после каждого хода и обязана быть незаметной, а подсказку человек
         * ждёт и готов подождать. */
        if (S.verdict === 'dead') {
            window.__toast && window.__toast(t('hint_none', 'Выигрышного хода отсюда нет'), 'info');
            return;
        }
        if (S.hintPending) return;
        askHintHard();
        return;
    }
    S.hints++;
    S.score = Math.max(0, S.score - 25);
    // Подсветка от прошлой подсказки живёт 700 мс. Нажатие подряд оставляло
    // мигать ЧЕТЫРЕ кости сразу — и человек не понимает, какая пара его.
    const board = $('mj-board');
    if (board) board.querySelectorAll('.mj-tile.mj-hint').forEach((e) => e.classList.remove('mj-hint'));
    const [a, b] = S.hintMove;
    flash(elFor(a), 'mj-hint');
    flash(elFor(b), 'mj-hint');
    setSelected(a);
    refresh();
    window.__aeFunnel && window.__aeFunnel('hint', { left: remaining(S.tiles) });
}

/** Явная просьба о подсказке: считаем дольше и говорим, что считаем. */
function askHintHard() {
    if (!S.worker) {
        window.__toast && window.__toast(t('hint_unavailable', 'Подсказка сейчас недоступна'), 'warning');
        return;
    }
    S.hintPending = true;
    window.__toast && window.__toast(t('hint_wait', 'Секунду, считаю…'), 'info');
    S.gen++;
    const asked = S.gen;
    S.hintResolve = (move) => {
        S.hintPending = false;
        if (S.gen !== asked) return;               // человек успел сходить
        if (move) { S.hintMove = move; useHint(); }
        else {
            window.__toast && window.__toast(
                S.verdict === 'dead' ? t('hint_none', 'Выигрышного хода отсюда нет')
                    : t('hint_unsure', 'Не удалось найти ход за отведённое время'), 'info');
        }
        window.__aeFunnel && window.__aeFunnel('hint_hard', { found: move ? 1 : 0, left: remaining(S.tiles) });
    };
    S.worker.postMessage({
        type: 'check', gen: asked,
        alive: Uint8Array.from(S.tiles, (x) => (x.removed ? 0 : 1)),
        budget: 4000000, deadlineMs: 6000, playouts: 900,
    });
    clearTimeout(S.hintTimer);
    S.hintTimer = setTimeout(() => {
        if (S.hintPending) {
            S.hintPending = false; S.hintResolve = null;
            window.__toast && window.__toast(t('hint_unsure', 'Не удалось найти ход за отведённое время'), 'info');
        }
    }, 9000);
}

/**
 * Перемешать остаток так, чтобы доска СНОВА СОБИРАЛАСЬ.
 *
 * Смена лиц у полутора сотен костей разом — единственное место в игре, где
 * переход между двумя состояниями действительно нужен: без него доска
 * «моргает» и непонятно, что произошло. View Transitions делает это одной
 * строкой, а там, где его нет или движение выключено, всё происходит мгновенно
 * — ровно как раньше.
 */
function doShuffle() {
    if (S.won) return;
    const apply = () => {
        const r = reshuffle(S.tiles, (Date.now() ^ S.seed) | 0);
        if (!r) {
            window.__toast && window.__toast(t('shuffle_failed', 'Перемешать не вышло — начните новую партию'), 'warning');
            return false;
        }
        S.shuffles++;
        S.score = Math.max(0, S.score - 60);
        S.history.length = 0;   // после перетасовки откат бессмыслен: доска другая
        S.redo.length = 0;
        S.lastSafe = 0;
        S.combo = 0;
        setSelected(null);
        hideDeadEnd();
        hideTip();
        buildBoard(false);
        startWorker();
        refresh();
        saveGame();
        return true;
    };
    let ok = true;
    if (window.__viewTransition) window.__viewTransition(() => { ok = apply(); });
    else ok = apply();
    if (ok !== false) {
        window.__toast && window.__toast(t('shuffle_ok', 'Перемешано — доска снова собирается'), 'success');
        window.__aeFunnel && window.__aeFunnel('shuffle', { n: S.shuffles, left: remaining(S.tiles) });
    }
}

/* ── экран ────────────────────────────────────────────────────────────── */

function refresh() {
    markFree();
    markPartners();
    const total = S.tiles.length;
    const left = remaining(S.tiles);
    const index = makeIndex(S.tiles);
    const moves = availableMoves(S.tiles, mg, index);
    // Пар, а не ходов: три свободные одинаковые кости дают три пары, но
    // человек видит одну возможность. Считаем группы.
    const groups = new Set(moves.map((m) => m.group)).size;
    setText('mj-left', String(left));
    setText('mj-pairs', String(groups));
    setText('mj-score', String(S.score));
    const done = total ? Math.round(((total - left) / total) * 100) : 0;
    const bar = $('mj-bar');
    if (bar) { bar.style.setProperty('--p', done); bar.setAttribute('aria-valuenow', String(done)); }

    const undoBtn = $('mj-undo'), redoBtn = $('mj-redo');
    if (undoBtn) undoBtn.disabled = S.won || !S.history.length;
    if (redoBtn) redoBtn.disabled = S.won || !S.redo.length;
    for (const id of ['mj-hint', 'mj-shuffle']) { const e = $(id); if (e) e.disabled = S.won; }

    const meta = layoutMeta(S.layout);
    const where = $('mj-where');
    if (where) {
        where.innerHTML = '';
        const b = document.createElement('b');
        b.textContent = t('layout_' + meta.id, meta.id) + (S.daily ? ' · ' + t('daily_tag', 'Задача дня') : '');
        where.appendChild(b);
        const s = document.createElement('span');
        s.textContent = t('n_tiles', '{n} костей').replace('{n}', String(meta.size))
            + ' · ' + t('lvl_' + S.level, S.level);
        where.appendChild(s);
    }

    if (groups === 0 && left > 0 && !S.won && settings.insurance) {
        const box = $('mj-dead');
        if (box && box.hidden) {
            box.hidden = false;
            $('mj-dead-text').textContent = t('stuck_text', 'Ходов больше нет. Можно откатить ход или перемешать остаток.');
            $('mj-rewind').hidden = S.history.length <= S.lastSafe;
        }
    }
}
const setText = (id, v) => { const e = $(id); if (e) e.textContent = v; };

function renderVerdict() {
    const el = $('mj-verdict'), txt = $('mj-verdict-t');
    if (!el || !txt) return;
    const map = {
        solvable: ['ok', t('v_solvable', 'Доска собирается')],
        dead: ['bad', t('v_dead', 'Выхода нет')],
        unknown: ['idk', t('v_unknown', 'Не берусь сказать')],
        checking: ['idk', t('v_checking', 'Проверяю…')],
    };
    const pair = map[S.verdict] || map.unknown;
    el.className = 'mj-verdict is-' + pair[0];
    txt.textContent = pair[1];
}

function tick() {
    if (S.ticking && !S.won) {
        S.elapsed = Math.floor((Date.now() - S.startedAt) / 1000);
        setText('mj-time', settings.timer ? clock(S.elapsed) : '—');
    }
    setTimeout(tick, 1000);
}
const clock = (s) => Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');

function win() {
    S.won = true;
    S.ticking = false;
    stopWorker();
    hideDeadEnd();
    hideTip();
    setWake(false);
    const rec = bumpStats();
    // Кнопки помощи гаснут вместе с партией: «отменить» на собранной доске
    // возвращало пару под карточку победы — состояние, из которого игра уже
    // не выходила.
    refresh();
    const box = $('mj-win');
    if (box) {
        box.hidden = false;
        setText('mj-win-time', settings.timer ? clock(S.elapsed) : '—');
        setText('mj-win-score', String(S.score));
        setText('mj-win-hints', String(S.hints));
        const bits = [];
        if (rec.bestTime) bits.push(t('rec_time', 'Новое лучшее время'));
        if (rec.bestScore) bits.push(t('rec_score', 'Новый рекорд по очкам'));
        if (S.daily && rec.streak > 1) bits.push(t('streak_n', 'Задача дня {n} дней подряд').replace('{n}', String(rec.streak)));
        $('mj-win-text').textContent = bits.length ? bits.join(' · ')
            : t('win_text', 'Ходов {moves}, перемешиваний {sh}')
                .replace('{moves}', String(S.history.length)).replace('{sh}', String(S.shuffles));
        const again = $('mj-win-again');
        if (again) setTimeout(() => { try { again.focus({ preventScroll: true }); } catch (e) { } }, 60);
    }
    blip(880, 0.12);
    setTimeout(() => blip(1180, 0.18), 130);
    vibe([16, 60, 26]);
    window.__aeToolComplete && window.__aeToolComplete({
        ok: true, layout: S.layout, size: S.tiles.length, seconds: S.elapsed,
        hints: S.hints, shuffles: S.shuffles, score: S.score, daily: S.daily ? 1 : 0,
    });
    clearSave();
}

/* ── статистика, рекорды, задача дня ──────────────────────────────────── */

const STATS = 'mj.stats';
const readStats = () => { try { return JSON.parse(localStorage.getItem(STATS) || '{}'); } catch (e) { return {}; } };
const writeStats = (s) => { try { localStorage.setItem(STATS, JSON.stringify(s)); } catch (e) { } };

function bumpStats() {
    const s = readStats();
    const k = S.layout;
    const r = s[k] = s[k] || { played: 0, won: 0, best: null, bestScore: 0 };
    r.won++;
    const bestTime = r.best === null || S.elapsed < r.best;
    if (bestTime) r.best = S.elapsed;
    const bestScore = S.score > (r.bestScore || 0);
    if (bestScore) r.bestScore = S.score;

    let streak = 0;
    if (S.daily) {
        s.daily = s.daily || {};
        const today = dayKey();
        if (s.daily.last !== today) {
            const y = dayKey(-1);
            s.daily.n = s.daily.last === y ? (s.daily.n || 0) + 1 : 1;
            s.daily.last = today;
        }
        streak = s.daily.n || 1;
    }
    writeStats(s);
    renderStats();
    return { bestTime, bestScore, streak };
}
function markPlayed(layout) {
    const s = readStats();
    s[layout] = s[layout] || { played: 0, won: 0, best: null, bestScore: 0 };
    s[layout].played++;
    writeStats(s);
}
function renderStats() {
    const box = $('mj-stats');
    if (!box) return;
    const s = readStats();
    const rows = LAYOUTS.filter((l) => s[l.id] && (s[l.id].played || s[l.id].won));
    box.textContent = '';
    if (!rows.length) {
        const p = document.createElement('p');
        p.className = 'mj-stats-empty';
        p.textContent = t('stats_empty', 'Пока пусто: рекорды появятся после первой собранной доски. Они хранятся в этом браузере.');
        box.appendChild(p);
        return;
    }
    const table = document.createElement('table');
    table.className = 'mj-stats-t';
    const head = document.createElement('thead');
    head.innerHTML = '<tr><th>' + esc(t('th_layout', 'Раскладка')) + '</th><th class="num">'
        + esc(t('th_played', 'Партий')) + '</th><th class="num">' + esc(t('th_won', 'Собрано'))
        + '</th><th class="num">' + esc(t('th_best', 'Лучшее время')) + '</th></tr>';
    table.appendChild(head);
    const body = document.createElement('tbody');
    for (const l of rows) {
        const r = s[l.id];
        const tr = document.createElement('tr');
        tr.innerHTML = '<th scope="row">' + esc(t('layout_' + l.id, l.id)) + '</th><td class="num">'
            + (r.played || 0) + '</td><td class="num">' + (r.won || 0) + '</td><td class="num">'
            + (r.best === null || r.best === undefined ? '—' : clock(r.best)) + '</td>';
        body.appendChild(tr);
    }
    table.appendChild(body);
    box.appendChild(table);
    if (s.daily && s.daily.n) {
        const p = document.createElement('p');
        p.className = 'mj-stats-empty';
        p.style.marginTop = '8px';
        p.textContent = t('streak_n', 'Задача дня {n} дней подряд').replace('{n}', String(s.daily.n));
        box.appendChild(p);
    }
}
const esc = (x) => String(x).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function dayKey(shift) {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() + (shift || 0));
    return d.toISOString().slice(0, 10);
}
/** Зерно задачи дня — из даты по UTC: у всех в мире одна и та же доска. */
function dailySeed() {
    const d = new Date();
    return (d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate()) | 0;
}
/** Раскладка задачи дня меняется по дням — иначе «задача» это только зерно. */
function dailyLayout() {
    const pool = LAYOUTS.filter((l) => l.pack !== 'quick');
    return pool[dailySeed() % pool.length].id;
}

/* ── сохранение ───────────────────────────────────────────────────────── */

const SAVE = 'mj.game';
function saveGame() {
    try {
        localStorage.setItem(SAVE, JSON.stringify({
            layout: S.layout, level: S.level, seed: S.seed, daily: S.daily,
            history: S.history, elapsed: Math.floor((Date.now() - S.startedAt) / 1000),
            score: S.score, hints: S.hints, shuffles: S.shuffles,
            ids: S.tiles.map((x) => x.id),
        }));
    } catch (e) { }
}
const clearSave = () => { try { localStorage.removeItem(SAVE); } catch (e) { } };

function restoreGame() {
    let g = null;
    try { g = JSON.parse(localStorage.getItem(SAVE) || 'null'); } catch (e) { }
    if (!g || !g.ids || !Array.isArray(g.history)) return false;
    if (!LAYOUTS.some((l) => l.id === g.layout)) return false;
    const pos = positionsOf(g.layout);
    // Рисунок раскладки мог измениться между версиями — тогда сохранение
    // относится к другой доске, и восстанавливать его нельзя.
    if (pos.length !== g.ids.length) return false;
    S.layout = g.layout; S.level = g.level || 'normal'; S.seed = g.seed || 0; S.daily = !!g.daily;
    S.pickLayout = S.layout; S.pickLevel = S.level;
    S.tiles = pos.map((p, i) => ({ x: p.x, y: p.y, z: p.z, i, id: g.ids[i], removed: false }));
    S.history = [];
    S.redo = [];
    for (const [ia, ib] of g.history) {
        if (!S.tiles[ia] || !S.tiles[ib]) continue;
        S.tiles[ia].removed = true; S.tiles[ib].removed = true;
        S.history.push([ia, ib]);
    }
    S.startedAt = Date.now() - (g.elapsed || 0) * 1000;
    S.score = g.score || 0; S.hints = g.hints || 0; S.shuffles = g.shuffles || 0;
    S.ticking = true; S.won = false; S.lastSafe = 0; S.selected = null; S.combo = 0;
    buildBoard(false);
    for (const [ia, ib] of S.history) {
        for (const i of [ia, ib]) { const el = elFor(i); if (el) el.classList.add('is-going', 'is-gone'); }
    }
    startWorker();
    refresh();
    window.__aeFunnel && window.__aeFunnel('game_restored', {
        layout: S.layout, left: remaining(S.tiles), moves: S.history.length,
    });
    return true;
}

/* ── выдвижные панели ─────────────────────────────────────────────────────
   Нативный popover сам кладёт панель в верхний слой, сам закрывает по Esc и
   по клику мимо, сам возвращает фокус на кнопку. Где его нет — тот же элемент
   показывается классом и своей подложкой; разметка одна, поэтому поведению
   негде разъехаться. Проверяем СПОСОБНОСТЬ, а не браузер. */
const canPopover = () => typeof HTMLElement !== 'undefined'
    && typeof HTMLElement.prototype.showPopover === 'function';

function openSheet(id) {
    const el = $(id);
    if (!el) return;
    el.classList.add('mj-open');
    if (canPopover()) {
        try { el.showPopover(); } catch (e) { showScrim(true); }
    } else showScrim(true);
    const first = el.querySelector('.mj-tab.on, .mj-b, button');
    if (first) setTimeout(() => { try { first.focus({ preventScroll: true }); } catch (e) { } }, 30);
    window.__aeFunnel && window.__aeFunnel('sheet_open', { sheet: id });
}
function closeSheet(id) {
    const el = $(id);
    if (!el) return;
    el.classList.remove('mj-open');
    if (canPopover()) { try { el.hidePopover(); } catch (e) { } }
    showScrim(false);
}
function showScrim(on) {
    const s = $('mj-scrim');
    if (!s) return;
    // Подложка нужна только запасному пути: у popover своя (::backdrop).
    s.hidden = !on || canPopover();
}
const anySheetOpen = () => !!document.querySelector('.mj-sheet.mj-open');

/* ── выбор раскладки ──────────────────────────────────────────────────── */

function renderPicker(pack) {
    const grid = $('mj-picker');
    if (!grid) return;
    const s = readStats();
    grid.textContent = '';
    for (const l of LAYOUTS) {
        if (pack !== 'all' && l.pack !== pack) continue;
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'mj-pick' + (l.id === S.pickLayout ? ' on' : '');
        b.dataset.layout = l.id;
        b.setAttribute('aria-pressed', l.id === S.pickLayout ? 'true' : 'false');

        const wrap = document.createElement('span');
        wrap.className = 'mj-thumbwrap';
        wrap.appendChild(thumbSvg(l.id));
        b.appendChild(wrap);

        const n = document.createElement('span');
        n.className = 'mj-pick-n';
        n.textContent = t('layout_' + l.id, l.id);
        b.appendChild(n);

        const sub = document.createElement('span');
        sub.className = 'mj-pick-s';
        sub.textContent = t('n_tiles', '{n} костей').replace('{n}', String(l.size));
        b.appendChild(sub);

        const rec = s[l.id];
        if (rec && rec.best !== null && rec.best !== undefined) {
            const best = document.createElement('span');
            best.className = 'mj-pick-best';
            best.textContent = clock(rec.best);
            b.appendChild(best);
        }
        grid.appendChild(b);
    }
}

function renderSkins() {
    const box = $('mj-skins');
    if (!box) return;
    box.textContent = '';
    for (const id of SKINS) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'mj-skin' + (id === settings.skin ? ' on' : '');
        b.dataset.skin = id;
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', id === settings.skin ? 'true' : 'false');
        const sw = document.createElement('span');
        sw.className = 'mj-swatch';
        // Образец рисуется теми же переменными, что и кость на доске: иначе
        // «предпросмотр» разъедется с результатом при первой правке темы.
        sw.style.background = ({
            ivory: 'linear-gradient(165deg,#fffdf6,#e6dfcb)',
            jade: 'linear-gradient(165deg,#f6fcf8,#c4dfd1)',
            night: 'linear-gradient(165deg,#3d4457,#252a38)',
            contrast: '#fff',
        })[id];
        if (id === 'contrast') sw.style.borderColor = '#000';
        b.appendChild(sw);
        const n = document.createElement('span');
        n.textContent = t('skin_' + id, id);
        b.appendChild(n);
        box.appendChild(b);
    }
}
function applySkin() {
    const root = $('mj');
    if (root) root.dataset.skin = settings.skin;
}

/* ── старт ────────────────────────────────────────────────────────────── */

function wire() {
    $('mj-board').addEventListener('click', onTileClick);

    const on = (id, fn) => { const e = $(id); if (e) e.addEventListener('click', fn); };
    on('mj-undo', () => undo(false));
    on('mj-redo', redoMove);
    on('mj-hint', useHint);
    on('mj-shuffle', doShuffle);
    on('mj-rewind', () => rewindToSafe());
    on('mj-dead-undo', () => undo(true));
    on('mj-dead-shuffle', doShuffle);
    on('mj-win-again', () => { hideWin(); newGame({ layout: S.layout, level: S.level }); });
    on('mj-fs', toggleFs);
    on('mj-open-pick', () => { S.pickLayout = S.layout; S.pickLevel = S.level; syncPickUi(); openSheet('mj-pick'); });
    on('mj-open-set', () => { renderStats(); openSheet('mj-set'); });

    document.querySelectorAll('[data-close]').forEach((b) => {
        b.addEventListener('click', () => closeSheet(b.dataset.close));
    });
    const scrim = $('mj-scrim');
    if (scrim) scrim.addEventListener('click', () => {
        document.querySelectorAll('.mj-sheet.mj-open').forEach((el) => closeSheet(el.id));
    });
    // Нативный popover закрывается сам (Esc, клик мимо) — класс обязан
    // сняться вместе с ним, иначе наш `display` переспорит стиль браузера и
    // панель останется висеть закрытой, но видимой.
    document.querySelectorAll('.mj-sheet').forEach((el) => {
        el.addEventListener('toggle', (ev) => {
            if (ev.newState === 'closed') { el.classList.remove('mj-open'); showScrim(false); }
        });
    });

    const picker = $('mj-picker');
    if (picker) picker.addEventListener('click', (ev) => {
        const b = ev.target.closest('.mj-pick');
        if (!b) return;
        S.pickLayout = b.dataset.layout;
        picker.querySelectorAll('.mj-pick').forEach((x) => {
            const sel = x === b;
            x.classList.toggle('on', sel);
            x.setAttribute('aria-pressed', sel ? 'true' : 'false');
        });
    });
    document.querySelectorAll('.mj-tab[data-pack]').forEach((b) => {
        b.addEventListener('click', () => {
            document.querySelectorAll('.mj-tab[data-pack]').forEach((x) => {
                x.classList.toggle('on', x === b);
                x.setAttribute('aria-selected', x === b ? 'true' : 'false');
            });
            renderPicker(b.dataset.pack);
        });
    });
    document.querySelectorAll('.mj-tab[data-level]').forEach((b) => {
        b.addEventListener('click', () => { S.pickLevel = b.dataset.level; syncLevelUi(); });
    });
    on('mj-start', () => {
        closeSheet('mj-pick');
        markPlayed(S.layout);
        const changed = S.pickLayout !== S.layout;
        window.__toolSettings && window.__toolSettings.save(SLUG, 'layout', S.pickLayout);
        window.__toolSettings && window.__toolSettings.save(SLUG, 'level', S.pickLevel);
        if (changed) {
            window.__aeFunnel && window.__aeFunnel('layout_change', { from: S.layout, to: S.pickLayout });
        }
        newGame({ layout: S.pickLayout, level: S.pickLevel });
    });
    on('mj-daily', () => {
        closeSheet('mj-pick');
        markPlayed(S.layout);
        newGame({ seed: dailySeed(), layout: dailyLayout(), daily: true, level: 'normal' });
    });

    /* `key` — имя в хранилище, `prop` — имя в объекте настроек. Они совпадают
       не всегда (`showfree` против `showFree`), и без разделения тумблер писал
       бы в постороннее свойство: настройка сохранялась бы и не применялась. */
    const bind = (id, key, prop, after) => {
        const el = $(id);
        if (!el) return;
        el.addEventListener('change', () => {
            settings[prop] = el.checked;
            window.__toolSettings && window.__toolSettings.save(SLUG, key, el.checked ? '1' : '0');
            if (after) after(el.checked);
        });
    };
    bind('mj-showfree', 'showfree', 'showFree', () => markFree());
    bind('mj-codes', 'codes', 'codes', (v) => $('mj-board').classList.toggle('mj-codes', v));
    bind('mj-sound', 'sound', 'sound');
    bind('mj-vibe', 'vibe', 'vibe');
    bind('mj-timer', 'timer', 'timer', () => setText('mj-time', settings.timer ? clock(S.elapsed) : '—'));
    bind('mj-wake', 'wake', 'wake', (v) => setWake(v && !S.won));
    bind('mj-insurance', 'insurance', 'insurance', (v) => {
        if (!v) hideDeadEnd();
        else if (S.verdict === 'dead') onDeadEnd();
    });
    const zoom = $('mj-zoom');
    if (zoom) zoom.addEventListener('input', () => {
        settings.zoom = +zoom.value;
        setText('mj-zoom-v', zoom.value + '%');
        fitBoard();
    });
    if (zoom) zoom.addEventListener('change', () => saveSetting('zoom', +zoom.value));

    const skins = $('mj-skins');
    if (skins) skins.addEventListener('click', (ev) => {
        const b = ev.target.closest('.mj-skin');
        if (!b) return;
        saveSetting('skin', b.dataset.skin);
        applySkin();
        renderSkins();
        window.__aeFunnel && window.__aeFunnel('skin', { skin: b.dataset.skin });
    });
    on('mj-stats-reset', () => {
        try { localStorage.removeItem(STATS); } catch (e) { }
        renderStats();
        renderPicker(currentPack());
        window.__toast && window.__toast(t('stats_cleared', 'Рекорды очищены'), 'success');
    });

    const onFsChange = () => {
        const real = !!(document.fullscreenElement || document.webkitFullscreenElement);
        if (!real && S.fsReal && fsActive()) exitFs(true);
        S.fsReal = real;
        fitBoard();
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    updateFsBtn();

    window.addEventListener('resize', fitBoard);
    window.addEventListener('orientationchange', () => setTimeout(fitBoard, 200));
    // Вкладка ушла в фон — часы останавливаем: иначе «лучшее время» копит
    // минуты, проведённые в другой вкладке, и рекорд перестаёт что-то значить.
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            S.elapsed = Math.floor((Date.now() - S.startedAt) / 1000);
            S.ticking = false;
        } else {
            S.startedAt = Date.now() - S.elapsed * 1000;
            S.ticking = !S.won;
        }
    });
    document.addEventListener('keydown', (e) => {
        if (e.target.matches('input,select,textarea')) return;
        if (anySheetOpen() && e.key !== 'Escape') return;
        const arrows = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
        if (arrows[e.key] && !e.ctrlKey && !e.metaKey && !e.altKey) {
            // Стрелки перехватываем ТОЛЬКО когда фокус уже на доске: иначе мы
            // отняли бы у страницы прокрутку у всех, кто просто читает.
            const onBoard = document.activeElement && document.activeElement.classList
                && document.activeElement.classList.contains('mj-tile');
            if (onBoard && moveFocus(arrows[e.key][0], arrows[e.key][1])) { e.preventDefault(); return; }
            if (onBoard) { e.preventDefault(); return; }
        }
        const kl = window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase();
        if (kl === 'z' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); undo(false); }
        else if (kl === 'y' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); redoMove(); }
        else if (kl === 'h' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); useHint(); }
        else if (kl === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); $('mj-open-pick').click(); }
        else if (kl === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); toggleFs(); }
        // Escape разматывает по одному уровню: сперва снимает выбор кости,
        // и только потом выходит из полноэкранного режима. Иначе игрок,
        // отменяющий случайный выбор, вылетал бы из режима целиком.
        else if (e.key === 'Escape') {
            if (anySheetOpen()) return;                 // панель закроется сама
            if (S.selected !== null) setSelected(null);
            else if (fsActive() && !document.fullscreenElement) exitFs(false);
        }
    });
}

const currentPack = () => {
    const on = document.querySelector('.mj-tab[data-pack].on');
    return on ? on.dataset.pack : 'all';
};

/* Сложность — выбор ОДНОГО из трёх, поэтому `aria-checked` у role=radio, а не
   `aria-selected` у вкладки: вкладка без своей панели объявляется скринридером
   как поломанная разметка. Состояние ставится в ОДНОМ месте — иначе начальная
   разметка и обработчик разъезжаются молча. */
function syncLevelUi() {
    document.querySelectorAll('.mj-tab[data-level]').forEach((x) => {
        const sel = x.dataset.level === S.pickLevel;
        x.classList.toggle('on', sel);
        x.setAttribute('aria-checked', sel ? 'true' : 'false');
    });
}

function syncPickUi() {
    renderPicker(currentPack());
    syncLevelUi();
    const lbl = $('mj-daily-lbl');
    const s = readStats();
    if (lbl && s.daily && s.daily.last === dayKey()) {
        lbl.textContent = t('daily_done', 'Задача дня собрана');
    }
}

function boot() {
    loadSettings();
    applySkin();
    renderSkins();

    const ts = window.__toolSettings;
    if (ts) {
        const l = ts.load(SLUG, 'layout', '');
        if (l && LAYOUTS.some((x) => x.id === l)) S.layout = l;
        const lv = ts.load(SLUG, 'level', '');
        if (lv === 'easy' || lv === 'normal' || lv === 'hard') S.level = lv;
    }
    S.pickLayout = S.layout; S.pickLevel = S.level;

    const set = (id, v) => { const e = $(id); if (e) e.checked = v; };
    set('mj-sound', settings.sound);
    set('mj-showfree', settings.showFree);
    set('mj-codes', settings.codes);
    set('mj-insurance', settings.insurance);
    set('mj-timer', settings.timer);
    set('mj-vibe', settings.vibe);
    set('mj-wake', settings.wake);
    const zoom = $('mj-zoom');
    if (zoom) { zoom.value = settings.zoom; setText('mj-zoom-v', settings.zoom + '%'); }
    // Строки настроек, которых эта платформа не умеет, не показываются вовсе:
    // тумблер, который заведомо ничего не сделает, — обещание, а не настройка.
    const vr = $('mj-vibe-row'); if (vr) vr.hidden = !canVibe();
    const wr = $('mj-wake-row'); if (wr) wr.hidden = !canWake();

    wire();
    observeBox();
    renderStats();
    syncPickUi();

    if (!restoreGame()) newGame({});
    tick();
    window.__stage && window.__stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
